import { Building2, FileText, Globe } from 'lucide-react';
import { useMemo } from 'react';

import type { SmsTemplateSchema } from '@/services/api/generated/smsTemplatesService';

import { SmsTemplateCard } from './SmsTemplateCard';

interface SmsTemplateListProps {
  templates: SmsTemplateSchema[];
  searchQuery?: string;
  categoryFilter?: string | null;
  onEdit?: (template: SmsTemplateSchema) => void;
  onDelete?: (template: SmsTemplateSchema) => void;
  canEdit?: boolean;
}

interface TemplateGroup {
  title: string;
  icon: React.ReactNode;
  templates: SmsTemplateSchema[];
}

export const SmsTemplateList = ({
  templates,
  searchQuery = '',
  categoryFilter = null,
  onEdit,
  onDelete,
  canEdit = false,
}: SmsTemplateListProps) => {
  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const matchesSearch =
        !searchQuery ||
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !categoryFilter || t.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [templates, searchQuery, categoryFilter]);

  const groups = useMemo<TemplateGroup[]>(() => {
    const globalTemplates = filteredTemplates.filter((t) => t.is_global);
    const firmTemplates = filteredTemplates.filter((t) => !t.is_global);

    const result: TemplateGroup[] = [];

    if (globalTemplates.length > 0) {
      result.push({
        title: 'Global Templates',
        icon: <Globe className="h-4 w-4" />,
        templates: globalTemplates,
      });
    }

    if (firmTemplates.length > 0) {
      result.push({
        title: 'Firm-Specific Templates',
        icon: <Building2 className="h-4 w-4" />,
        templates: firmTemplates,
      });
    }

    return result;
  }, [filteredTemplates]);

  if (filteredTemplates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-12 w-12 text-zinc-300 mb-3" />
        <h3 className="text-sm font-medium text-zinc-900">
          {searchQuery || categoryFilter
            ? 'No matching templates'
            : 'No templates yet'}
        </h3>
        <p className="text-sm text-zinc-500 mt-1">
          {searchQuery || categoryFilter
            ? 'Try adjusting your search or filters'
            : 'Create your first SMS template to get started'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.title}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-zinc-400">{group.icon}</span>
            <h3 className="text-sm font-medium text-zinc-700">{group.title}</h3>
            <span className="text-xs text-zinc-400">
              ({group.templates.length})
            </span>
          </div>
          <div className="space-y-3">
            {group.templates.map((template) => (
              <SmsTemplateCard
                key={template.id}
                template={template}
                onEdit={onEdit}
                onDelete={onDelete}
                canEdit={canEdit}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
